import React from 'react';
import { Table, TableColumn, Checkbox } from 'common/components';
import { Contact } from 'types/contact.type';
import styles from './ContactManager.module.scss';

interface ContactsTableProps {
    contacts: Contact[];
    loading: boolean;
    selectedContacts: string[];
    areAllSelected: boolean;
    searchTerm?: string;
    totalRecords: number;
    currentPage: number;
    totalPages: number;
    rowsPerPage: number;
    onRowClick: (contact: Contact) => void;
    onContactSelect: (contactId: string) => void;
    onSelectAll: () => void;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (newRowsPerPage: number) => void;
}

const ContactsTable: React.FC<ContactsTableProps> = ({
    contacts,
    loading,
    selectedContacts,
    areAllSelected,
    searchTerm,
    totalRecords,
    currentPage,
    totalPages,
    rowsPerPage,
    onRowClick,
    onContactSelect,
    onSelectAll,
    onPageChange,
    onRowsPerPageChange,
}) => {
    // Format date to display in a readable format
    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    // Render status badge with dynamically assigned color
    const renderStatusBadge = (status: string): React.ReactElement | null => {
        if (!status) return null;

        const colorPalette = ['new', 'contacted', 'qualified', 'attemptMade', 'unreachable', 'inProgress', 'completed'];

        const getColorClass = (str: string): string => {
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                hash = (hash << 5) - hash + str.charCodeAt(i);
                hash = hash & hash;
            }
            const index = Math.abs(hash) % colorPalette.length;
            return colorPalette[index];
        };

        const colorClass = getColorClass(status.toLowerCase());
        const displayStatus = status.replace(/_/g, ' ');

        return <span className={`${styles.statusBadge} ${styles[colorClass]}`}>{displayStatus}</span>;
    };

    // Check if some (but not all) contacts are selected for indeterminate state
    const areSomeSelected =
        contacts.length > 0 &&
        contacts.some((contact) => selectedContacts.includes(contact.contactId)) &&
        !areAllSelected;

    // Define table columns
    const columns: TableColumn<Contact>[] = [
        {
            key: 'checkbox',
            title: (
                <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                        checked={areAllSelected}
                        indeterminate={areSomeSelected}
                        onChange={onSelectAll}
                        disabled={contacts.length === 0}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            ),
            width: 40,
            align: 'center',
            render: (_, record) => (
                <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                        checked={selectedContacts.includes(record.contactId)}
                        onChange={() => onContactSelect(record.contactId)}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            ),
        },
        {
            key: 'crmId',
            title: 'CRM ID',
            dataIndex: 'crmId',
            render: (value) => value || '',
        },
        {
            key: 'contactName',
            title: 'Contact Name',
            dataIndex: 'contactName',
        },
        {
            key: 'contactPhone',
            title: 'Phone',
            dataIndex: 'contactPhone',
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'status',
            render: (value) => renderStatusBadge(value),
        },
        {
            key: 'source',
            title: 'Source',
            dataIndex: 'source',
        },
        {
            key: 'createdAt',
            title: 'Created',
            dataIndex: 'createdAt',
            render: (value) => formatDate(value),
        },
    ];

    return (
        <div className={styles.tableContainer}>
            {searchTerm && (
                <div className={styles.searchResultsInfo}>
                    Found {totalRecords} results for "{searchTerm}"
                </div>
            )}
            <Table
                data={contacts}
                columns={columns}
                isLoading={loading}
                emptyText='No contacts found matching your filters.'
                rowKey='contactId'
                onRowClick={(contact) => onRowClick(contact)}
                hoverable
                variant='secondary'
                pagination={
                    totalRecords > 0
                        ? {
                              enabled: true,
                              currentPage,
                              totalPages,
                              rowsPerPage,
                              onPageChange,
                              onRowsPerPageChange,
                              rowsPerPageOptions: [5, 10, 25, 50],
                          }
                        : undefined
                }
            />
        </div>
    );
};

export default ContactsTable;
